import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { Eye, Filter, Search, Mail, User, MapPin, Download } from 'lucide-react';
import { collection, query, orderBy, getDocs, where, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { QuoteRequest, QuoteStatus, QuoteType } from '@/types';
import { formatCurrency, formatDate, formatPhone } from '@/lib/utils';

export default function QuoteRequestsList() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  // Load quote requests
  useEffect(() => {
    if (!user || role !== 'admin') {
      return;
    }

    const loadQuotes = async () => {
      try {
        setLoading(true);

        let q = query(
          collection(db, 'quoteRequests'),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);

        const quotesData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
            updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
            quotedAt: (data.quotedAt as Timestamp)?.toDate(),
            expiresAt: (data.expiresAt as Timestamp)?.toDate(),
          } as QuoteRequest;
        });

        setQuotes(quotesData);
      } catch (error) {
        console.error('[QuoteRequestsList] Error loading quotes:', error);
        toast({
          title: 'Error',
          description: 'Failed to load quote requests. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadQuotes();
  }, [user, role, toast]);

  // Filter quotes
  const filteredQuotes = quotes.filter((quote) => {
    // Status filter
    if (filterStatus !== 'all' && quote.status !== filterStatus) {
      return false;
    }

    // Type filter
    if (filterType !== 'all' && quote.type !== filterType) {
      return false;
    }

    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        quote.guardianName.toLowerCase().includes(search) ||
        quote.guardianEmail.toLowerCase().includes(search) ||
        quote.pickupAddress.toLowerCase().includes(search) ||
        quote.dropoffAddress.toLowerCase().includes(search) ||
        quote.dropoffZipcode.includes(search)
      );
    }

    return true;
  });

  // Get counts for status badges
  const statusCounts = {
    all: quotes.length,
    pending: quotes.filter((q) => q.status === 'pending').length,
    quoted: quotes.filter((q) => q.status === 'quoted').length,
    accepted: quotes.filter((q) => q.status === 'accepted').length,
    expired: quotes.filter((q) => q.status === 'expired').length,
  };

  const getStatusColor = (status: QuoteStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'quoted':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: QuoteStatus) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getTypeLabel = (type: QuoteType) => {
    const labels: Record<QuoteType, string> = {
      'hubstop': 'Hubstop',
      'yearly': 'Yearly',
      'summer-camp': 'Summer Camp',
      'custom': 'Custom',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: QuoteType) => {
    const colors: Record<QuoteType, string> = {
      'hubstop': 'bg-purple-100 text-purple-800',
      'yearly': 'bg-indigo-100 text-indigo-800',
      'summer-camp': 'bg-orange-100 text-orange-800',
      'custom': 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const exportUnbookedCSV = () => {
    const unbooked = filteredQuotes.filter(q => q.status !== 'accepted');

    const headers = [
      'Quote ID',
      'Status',
      'Type',
      'Submitted At',
      'Guardian Name',
      'Guardian Email',
      'Guardian Phone',
      'Pickup Address',
      'Pickup Zipcode',
      'Dropoff Address',
      'Dropoff Zipcode',
      'Hubstop',
      'Students',
      'Sessions / Weeks Selected',
      'Calculated Price',
      'Route Name',
      'Linked Offer ID',
    ];

    const rows = unbooked.map(q => [
      q.id,
      getStatusLabel(q.status),
      getTypeLabel(q.type),
      formatDate(q.createdAt),
      q.guardianName,
      q.guardianEmail,
      q.guardianPhone,
      q.pickupAddress,
      q.pickupZipcode,
      q.dropoffAddress,
      q.dropoffZipcode,
      q.hubstopName ?? '',
      q.students.length.toString(),
      q.selectedSessions?.join(', ') ?? '',
      q.calculatedPrice?.toString() ?? '',
      q.routeName ?? '',
      q.offerId ?? '',
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `unbooked-families-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading quote requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quote Requests</h1>
            <p className="text-gray-600 mt-1">
              Review and respond to customer transportation quote requests
            </p>
          </div>
          <Button
            variant="outline"
            onClick={exportUnbookedCSV}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Unbooked
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{statusCounts.all}</div>
              <div className="text-sm text-gray-600">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{statusCounts.quoted}</div>
              <div className="text-sm text-gray-600">Quoted</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{statusCounts.accepted}</div>
              <div className="text-sm text-gray-600">Accepted</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-600">{statusCounts.expired}</div>
              <div className="text-sm text-gray-600">Expired</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by guardian name, email, or destination..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Status:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'pending', 'quoted', 'accepted', 'expired'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filterStatus === status
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {getStatusLabel(status as QuoteStatus)} ({status === 'all' ? statusCounts.all : statusCounts[status as keyof typeof statusCounts]})
                  </button>
                ))}
              </div>
            </div>

            {/* Type Filter */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Type:</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['all', 'summer-camp', 'hubstop', 'yearly', 'custom'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      filterType === type
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type === 'all' ? 'All Types' : getTypeLabel(type as QuoteType)}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotes List */}
        {filteredQuotes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <Mail className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery || filterStatus !== 'all' || filterType !== 'all'
                  ? 'No matching quote requests'
                  : 'No quote requests yet'}
              </h3>
              <p className="text-gray-600">
                {searchQuery || filterStatus !== 'all' || filterType !== 'all'
                  ? 'Try adjusting your filters or search query.'
                  : 'Quote requests will appear here when customers submit them.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/admin/quote-requests/${quote.id}`)}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header with badges */}
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                          {getStatusLabel(quote.status)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(quote.type)}`}>
                          {getTypeLabel(quote.type)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(quote.createdAt)}
                        </span>
                      </div>

                      {/* Guardian Info */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">{quote.guardianName}</span>
                        </div>
                        <div className="text-sm text-gray-600 ml-6">
                          {quote.guardianEmail} • {formatPhone(quote.guardianPhone)}
                        </div>
                      </div>

                      {/* Route Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-700">Pickup</div>
                              <div className="text-gray-600">{quote.pickupAddress}</div>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <div className="font-medium text-gray-700">Dropoff</div>
                              <div className="text-gray-600">{quote.dropoffAddress}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-gray-100">
                        <div>
                          <span className="font-medium text-gray-700">Students:</span>{' '}
                          <span className="text-gray-900">{quote.students.length}</span>
                        </div>
                        {quote.calculatedPrice && (
                          <div>
                            <span className="font-medium text-gray-700">Price:</span>{' '}
                            <span className="text-gray-900 font-semibold">{formatCurrency(quote.calculatedPrice)}</span>
                          </div>
                        )}
                        {quote.hubstopName && (
                          <div>
                            <span className="font-medium text-gray-700">Hubstop:</span>{' '}
                            <span className="text-gray-900">{quote.hubstopName}</span>
                          </div>
                        )}
                        {quote.selectedSessions && quote.selectedSessions.length > 0 && (
                          <div>
                            <span className="font-medium text-gray-700">Weeks:</span>{' '}
                            <span className="text-gray-900">{quote.selectedSessions.length}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/quote-requests/${quote.id}`);
                      }}
                      className="flex items-center gap-1 ml-4"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
